import { COUNTRIES } from './countries';
import type { ICountry } from './i-country';

/** Union of all known ISO 3166-1 alpha-2 country codes. */
export type CountryCode = (typeof COUNTRIES)[number]['code'];

/**
 * ISO 3166-1 alpha-2 country codes as an enum-compatible object (key === value).
 * Use this for GraphQL enum registration and @Field() decorators.
 */
export const CountryCode = Object.fromEntries(COUNTRIES.map(c => [c.code, c.code])) as {
  readonly [K in CountryCode]: K;
};

const _countryMap = new Map<string, (typeof COUNTRIES)[number]>(COUNTRIES.map(c => [c.code, c]));

/**
 * Returns the ICountry for the given ISO 3166-1 alpha-2 code, or undefined if not found.
 */
export function getCountry(code: string | null | undefined): ICountry | undefined {
  const normalized = code?.toUpperCase().trim();
  if (!normalized) return undefined;
  return _countryMap.get(normalized);
}

/**
 * Resolves a code string (any casing/whitespace) or ICountry to its canonical CountryCode.
 * Returns undefined for blank or unknown input.
 */
export function normalizeCountryCode(value: string | null | undefined): CountryCode | undefined {
  const normalized = value?.toUpperCase().trim();
  if (!normalized) return undefined;
  return _countryMap.get(normalized)?.code;
}

/**
 * Strict type guard — true only for a canonical uppercase known code (no normalization).
 */
export function isCountryCode(value: unknown): value is CountryCode {
  return typeof value === 'string' && _countryMap.has(value);
}
